import type { QueryClient, QueryKey } from '@tanstack/solid-query';
import { queryKeys } from '~/shared/api/query-keys';

export type MoneyMove =
  | 'tournament'
  | 'booking'
  | 'split'
  | 'clientBalance'
  | 'refund'
  | 'sale'
  | 'subscription'
  | 'groupSeat'
  | 'organization'
  | 'platformInvoice';

export interface MoneyIds {
  tournamentId?: string;
}

const FINANCE: readonly QueryKey[] = [['finance']];

const CLIENT: readonly QueryKey[] = [
  ['client'],
  ['clients'],
  ['wallet-statement'],
  ['wallet-topups'],
  ['bonuses'],
  ['client-subscriptions'],
];

const BOOKING: readonly QueryKey[] = [['booking'], ['bookings'], ['schedule'], ['schedule-range']];

function keysOfMove(move: MoneyMove, ids: MoneyIds): readonly QueryKey[] {
  switch (move) {
    case 'tournament':
      return [
        ids.tournamentId ? queryKeys.tournament.detail(ids.tournamentId) : ['tournament'],
        queryKeys.tournament.all(),
        ...FINANCE,
        ...CLIENT,
      ];
    case 'booking':
      return [...BOOKING, ...FINANCE, ...CLIENT];
    case 'split':
      return [['booking-split'], ...BOOKING, ...FINANCE, ...CLIENT];
    case 'clientBalance':
      return [...FINANCE, ...CLIENT];
    case 'refund':
      return [...FINANCE, ...CLIENT, ...BOOKING];
    case 'sale':
      return [['pos'], ['dashboard'], ...FINANCE, ...CLIENT, ...BOOKING];
    case 'subscription':
      return [['client-subscriptions'], ...FINANCE, ...CLIENT];
    case 'groupSeat':
      return [['group-sessions'], ...FINANCE, ...CLIENT];
    case 'organization':
      return [['organizations'], ...FINANCE];
    case 'platformInvoice':
      return [['platform'], ['billing']];
  }
}

export function moneyKeysOf(move: MoneyMove, ids: MoneyIds = {}): QueryKey[] {
  const seen = new Set<string>();
  return keysOfMove(move, ids).filter((key) => {
    const id = JSON.stringify(key);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

const CLUB_MOVES: readonly MoneyMove[] = [
  'tournament',
  'booking',
  'split',
  'clientBalance',
  'refund',
  'sale',
  'subscription',
  'groupSeat',
  'organization',
];

export const ALL_CLUB_MONEY_KEYS: readonly QueryKey[] = (() => {
  const seen = new Set<string>();
  return CLUB_MOVES.flatMap((move) => moneyKeysOf(move)).filter((key) => {
    const id = JSON.stringify(key);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
})();

export function refreshAfterMoney(queryClient: QueryClient, move: MoneyMove, ids: MoneyIds = {}): void {
  for (const queryKey of moneyKeysOf(move, ids)) void queryClient.invalidateQueries({ queryKey });
}
